import { Link } from '@tanstack/react-router'
import type { Agent } from '@/lib/api'
import { GlassCard } from '@/components/ui/GlassCard'
import { AgentStatusBadge } from '@/components/ui/GlassBadge'
import { GlassButton } from '@/components/ui/GlassButton'
import { Bot, MessageSquare, Settings2 } from 'lucide-react'
import { useT } from '@/i18n/provider'

interface AgentCardProps {
  agent: Agent
}

export function AgentCard({ agent }: AgentCardProps) {
  const t = useT()

  return (
    <GlassCard hover className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--oc-accent)]/15 border border-[var(--oc-accent)]/25 flex items-center justify-center flex-shrink-0">
            <Bot size={20} className="text-[var(--oc-accent-light)]" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm leading-tight">{agent.name}</h3>
            {agent.aiConfig && (
              <p className="text-xs text-white/40 mt-0.5">
                {agent.aiConfig.providerId} · {agent.aiConfig.modelId}
              </p>
            )}
          </div>
        </div>
        <AgentStatusBadge status={agent.status} />
      </div>

      {/* Description */}
      {agent.description && (
        <p className="text-sm text-white/50 leading-relaxed line-clamp-2">
          {agent.description}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-auto pt-2 border-t border-white/8">
        <Link to="/dashboard/agents/$agentId" params={{ agentId: agent.id }} className="flex-1">
          <GlassButton size="sm" variant="secondary" className="w-full gap-1.5">
            <MessageSquare size={13} />
            {t('agents.detail.chatButton')}
          </GlassButton>
        </Link>
        <Link to="/dashboard/agents/$agentId" params={{ agentId: agent.id }} search={{ tab: 'settings' }}>
          <GlassButton size="sm" variant="ghost">
            <Settings2 size={13} />
          </GlassButton>
        </Link>
      </div>
    </GlassCard>
  )
}
